/**
 * Working without signal. The service worker keeps the app itself; this keeps the last copy of the
 * screens people need in the field (Today, Cattle, an animal, its history) so they open read-only
 * when the network is gone. Saved copies belong to one ranch and person, live only in this browser,
 * and are cleared on sign out. Writes are never replayed from here: notes go through the outbox.
 */
import { useSyncExternalStore } from "react";

export interface Snapshot { path: string; data: unknown; saved_at: string }

/** GET paths worth keeping. Anything else simply fails offline with a plain message. */
const KEEP = [/^\/me$/, /^\/today$/, /^\/animals(\?|$)/, /^\/animals\/[\w-]+(\/pedigree)?(\?|$)/, /^\/groups(\?|$)/, /^\/groups\/[\w-]+$/, /^\/places(\?|$)/, /^\/places\/[\w-]+$/,
  /^\/events\?/, /^\/tasks(\?|$)/, /^\/jobs(\?|$)/, /^\/jobs\/[\w-]+$/, /^\/captures\?/];
export const keepsSnapshot = (path: string) => KEEP.some((r) => r.test(path));

const DB = "hondo-v2-saved";
const STORE = "snapshots";
const MAX = 80;
const memory = new Map<string, Snapshot>();
let owner = "";

/** Scope saved copies to the signed-in ranch and person. `/me` itself is kept under its own key. */
export function setSnapshotOwner(next: string): void { owner = next; }
const keyFor = (path: string) => (path === "/me" ? "me" : `${owner}|${path}`);

function open(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore(STORE);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch { resolve(null); }
  });
}

async function withStore<T>(mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | null> {
  const db = await open();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const r = fn(tx.objectStore(STORE));
      tx.oncomplete = () => { resolve(r ? r.result : null); db.close(); };
      tx.onerror = () => { resolve(null); db.close(); };
    } catch { resolve(null); }
  });
}

export async function writeSnapshot(path: string, data: unknown): Promise<void> {
  if (!keepsSnapshot(path) || (path !== "/me" && !owner)) return;
  const snap: Snapshot = { path, data, saved_at: new Date().toISOString() };
  const key = keyFor(path);
  memory.set(key, snap);
  await withStore("readwrite", (s) => { s.put(snap, key); });
  if (memory.size > MAX) for (const k of [...memory.keys()].slice(0, memory.size - MAX)) memory.delete(k);
}

export async function readSnapshot(path: string): Promise<Snapshot | null> {
  if (!keepsSnapshot(path)) return null;
  const key = keyFor(path);
  const stored = await withStore<Snapshot | undefined>("readonly", (s) => s.get(key));
  return stored ?? memory.get(key) ?? null;
}

/** Forget every saved copy (sign out, switching accounts, or the person asks). */
export async function clearSnapshots(): Promise<void> {
  memory.clear();
  await withStore("readwrite", (s) => { s.clear(); });
}

/** Trim the store so it never grows without bound. */
export async function pruneSnapshots(): Promise<void> {
  const db = await open();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE, "readwrite");
      const store = tx.objectStore(STORE);
      const req = store.getAllKeys();
      req.onsuccess = () => {
        const all = store.getAll();
        all.onsuccess = () => {
          const rows = (all.result as Snapshot[]).map((s, i) => ({ key: req.result[i], at: s.saved_at })).sort((a, b) => b.at.localeCompare(a.at));
          for (const r of rows.slice(MAX)) store.delete(r.key);
        };
      };
      tx.oncomplete = () => { db.close(); resolve(); };
      tx.onerror = () => { db.close(); resolve(); };
    } catch { resolve(); }
  });
}

// ---------------------------------------------------------------- connection state shown in the banner

export interface ConnectionState {
  /** The browser says it is offline, or the last request couldn't reach Hondo. */
  offline: boolean;
  /** Oldest saved copy currently on screen, when anything came from this browser instead of the network. */
  savedAt: string | null;
}

let state: ConnectionState = { offline: typeof navigator !== "undefined" ? navigator.onLine === false : false, savedAt: null };
const listeners = new Set<() => void>();
const emit = (next: ConnectionState) => { if (next.offline === state.offline && next.savedAt === state.savedAt) return; state = next; listeners.forEach((l) => l()); };

export const getConnection = () => state;
export function setConnection(next: Partial<ConnectionState>): void { emit({ ...state, ...next }); }

/** A request couldn't reach the server. */
export function noteNetworkFailure(): void { emit({ ...state, offline: true }); }
/** A request reached the server: anything on screen is fresh again soon. */
export function noteNetworkSuccess(): void { if (state.offline || state.savedAt) emit({ offline: false, savedAt: null }); }
/** Something on screen came from a saved copy. */
export function noteSnapshotUsed(savedAt: string): void {
  emit({ offline: true, savedAt: state.savedAt && state.savedAt < savedAt ? state.savedAt : savedAt });
}

export function useConnection(): ConnectionState {
  return useSyncExternalStore((l) => { listeners.add(l); return () => listeners.delete(l); }, getConnection, getConnection);
}

if (typeof window !== "undefined") {
  window.addEventListener("offline", () => emit({ ...state, offline: true }));
  window.addEventListener("online", () => emit({ ...state, offline: false }));
}
